import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { ApiError, apiFetch } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import { COMMERCE_CAPABILITIES, hasCommerceCapability } from '../../platform/commerceCapabilities';
import { listCompanies, type Company } from './companiesApi';
import '../_shared/shared.css';
import './companies.css';

interface DeviceBatch {
  batch_id: string;
  company_id: string;
  device_ids?: string[];
  invoice_id?: string;
  status?: string;
  description?: string;
  created_by?: string;
  created_at?: number;
  voided_at?: number;
}

interface CompanyInvoice {
  invoice_id: string;
  company_id: string;
  batch_id?: string;
  device_ids?: string[];
  currency?: string;
  amount_cents?: number;
  status?: string;
  description?: string;
  created_at?: number;
}

interface BatchResult {
  batch: DeviceBatch;
  invoice: CompanyInvoice;
}

type OrgTab = 'profile' | 'batches' | 'invoices';

interface CompanyBrowseRequest {
  cursor: string;
  stack: string[];
  search: string;
}

// 只把人話 message 呈現給操作者;error code 屬除錯資訊,改以 request_id 追查。
function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

// 狀態 enum 中文化(值來自後端 enum,顯示層全中文;送 API/比較仍用原 enum)
const STATUS_LABELS: Record<string, string> = {
  active: '使用中',
  void: '已作廢',
  voided: '已作廢',
  paid: '已付款',
  open: '開立中',
  suspended: '暫停',
};

function statusLabel(status?: string): string {
  if (!status) return '—';
  return STATUS_LABELS[status] ?? status;
}

function money(inv: CompanyInvoice): string {
  const cents = inv.amount_cents ?? 0;
  return `${inv.currency || 'TWD'} ${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function invoiceTotal(invoices: CompanyInvoice[]): string {
  if (invoices.length === 0) return '—';
  const currency = invoices[0].currency || 'TWD';
  if (invoices.some((inv) => (inv.currency || 'TWD') !== currency)) return '幣別混合';
  const cents = invoices.reduce((sum, inv) => sum + (inv.amount_cents ?? 0), 0);
  return `${currency} ${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusTone(status?: string): string {
  if (!status) return 'pill-neutral';
  if (status === 'active' || status === 'paid' || status === 'open') return 'pill-good';
  if (status === 'void' || status === 'voided' || status === 'suspended') return 'pill-warn';
  return 'pill-neutral';
}

function OrgStatus({ status }: { status?: string }) {
  return <span className={`pill ${statusTone(status)}`}>{statusLabel(status)}</span>;
}

// 是否為本月建立(供上方摘要統計列客戶端計算,不打新 API)
function isThisMonth(us?: number): boolean {
  if (!us) return false;
  const created = new Date(us / 1000); // created_at 為微秒(microseconds),換算成毫秒
  const now = new Date();
  return created.getFullYear() === now.getFullYear() && created.getMonth() === now.getMonth();
}

const COMPANIES_PAGE_SIZE = 50;

export default function CompaniesPage() {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const canViewInvoices = hasCommerceCapability(role, COMMERCE_CAPABILITIES.commerceRead);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selected, setSelected] = useState<Company | null>(null);
  const [batches, setBatches] = useState<DeviceBatch[]>([]);
  const [invoices, setInvoices] = useState<CompanyInvoice[]>([]);
  const [activeTab, setActiveTab] = useState<OrgTab>('profile');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [currentCursor, setCurrentCursor] = useState('');
  const [failedBrowseRequest, setFailedBrowseRequest] = useState<CompanyBrowseRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');
  // 列表載入錯誤(原始物件,供 ErrorState 判斷 403);與行內動作錯誤 err 分離,避免落到假空狀態
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const workbenchRef = useRef<HTMLElement | null>(null);

  const [name, setName] = useState('');
  const [taxId, setTaxId] = useState('');
  const [country, setCountry] = useState('TW');
  const [contact, setContact] = useState('');
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const createCardRef = useRef<HTMLDivElement | null>(null);

  const [deviceIds, setDeviceIds] = useState('');
  const [amount, setAmount] = useState('0');
  const [currency, setCurrency] = useState('TWD');
  const [desc, setDesc] = useState('');
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [voidTarget, setVoidTarget] = useState<DeviceBatch | null>(null); // 作廢二次確認目標

  const batchDeviceCount = useMemo(() => batches.reduce((sum, batch) => sum + (batch.device_ids?.length ?? 0), 0), [batches]);
  const activeBatchCount = useMemo(() => batches.filter((batch) => batch.status !== 'voided').length, [batches]);
  const hasContext = reason.trim() && caseId.trim();

  // 後端目前不回 total；摘要一律明示「本頁」，不拿已載入筆數假裝全站總數。
  const activeCompanyCount = useMemo(() => companies.filter((c) => c.status === 'active').length, [companies]);
  const newCompanyThisMonth = useMemo(() => companies.filter((c) => isThisMonth(c.created_at)).length, [companies]);

  async function loadCompanies(cursor = '', stack = cursorStack, search = query) {
    const request: CompanyBrowseRequest = { cursor, stack: [...stack], search };
    setBusy(true);
    setErr('');
    setLoadErr(null);
    setFailedBrowseRequest(null);
    try {
      const page = await listCompanies({ query: request.search, cursor: request.cursor, limit: COMPANIES_PAGE_SIZE });
      setCompanies(page.companies);
      setNextCursor(page.nextCursor);
      setCurrentCursor(request.cursor);
      setCursorStack(request.stack);
    } catch (e) {
      // 載入失敗改由 ErrorState 呈現(含 403 無權限 + request_id + 重試),不落到假空狀態
      setFailedBrowseRequest(request);
      setLoadErr(e);
    } finally {
      setBusy(false);
    }
  }

  async function loadCompanyDetail(company: Company, tab: OrgTab = 'profile') {
    setSelected(company);
    setActiveTab(tab === 'invoices' && !canViewInvoices ? 'profile' : tab);
    setErr('');
    setNotice('');
    setDetailLoading(true);
    try {
      const b = await apiFetch<{ batches?: DeviceBatch[] }>(`/v3/admin/companies/${encodeURIComponent(company.company_id)}/batches`);
      setBatches(Array.isArray(b.data?.batches) ? b.data.batches : []);
      if (canViewInvoices) {
        const inv = await apiFetch<{ invoices?: CompanyInvoice[] }>(`/v3/admin/companies/${encodeURIComponent(company.company_id)}/invoices`);
        setInvoices(Array.isArray(inv.data?.invoices) ? inv.data.invoices : []);
      } else {
        setInvoices([]);
      }
    } catch (e) {
      setErr(errorText(e));
      setBatches([]);
      setInvoices([]);
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    void loadCompanies('', [], query);
  }, [query]);

  function submitSearch(e: FormEvent) {
    e.preventDefault();
    const next = queryInput.trim();
    if (next === query) {
      void loadCompanies('', [], next);
      return;
    }
    setQuery(next);
  }

  function clearSearch() {
    setQueryInput('');
    if (query === '') {
      void loadCompanies('', [], '');
    } else {
      setQuery('');
    }
  }

  function nextPage() {
    if (!nextCursor) return;
    void loadCompanies(nextCursor, [...cursorStack, currentCursor]);
  }

  function prevPage() {
    if (cursorStack.length === 0) return;
    const nextStack = cursorStack.slice(0, -1);
    const cursor = cursorStack[cursorStack.length - 1] ?? '';
    void loadCompanies(cursor, nextStack);
  }

  function retryCompanies() {
    const request = failedBrowseRequest ?? { cursor: currentCursor, stack: cursorStack, search: query };
    void loadCompanies(request.cursor, request.stack, request.search);
  }

  // 選取公司後,詳情 workbench 在頁尾、易被忽略 → 捲入視窗頂端;selected 初始為 null,不會在首載觸發
  useEffect(() => {
    if (selected) workbenchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selected?.company_id]);

  function focusCreateForm() {
    createCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    nameInputRef.current?.focus();
  }

  async function createCompany() {
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await apiFetch<Company>('/v3/admin/companies', {
        method: 'POST',
        body: { name: name.trim(), tax_id: taxId.trim(), country: country.trim(), contact: contact.trim() },
      });
      setName('');
      setTaxId('');
      setContact('');
      setNotice(`已建立公司 ${res.data.name}。`);
      setQueryInput('');
      setQuery('');
      await loadCompanies('', [], '');
      await loadCompanyDetail(res.data);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function createBatch() {
    if (!selected) return;
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const ids = deviceIds
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await apiFetch<BatchResult>('/v3/admin/batches', {
        method: 'POST',
        body: {
          company_id: selected.company_id,
          device_ids: ids,
          amount_cents: Math.round(Number(amount || '0') * 100),
          currency,
          description: desc.trim(),
          reason: reason.trim(),
          case_id: caseId.trim(),
        },
      });
      setBatches((rows) => [res.data.batch, ...rows]);
      if (canViewInvoices) setInvoices((rows) => [res.data.invoice, ...rows]);
      setDeviceIds('');
      setDesc('');
      setNotice(`已建立出貨批次 ${res.data.batch.batch_id}。`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function voidBatch(batchId: string) {
    setBusy(true);
    setErr('');
    setNotice('');
    try {
      const res = await apiFetch<DeviceBatch>(`/v3/admin/batches/${encodeURIComponent(batchId)}/void`, {
        method: 'POST',
        body: { reason: reason.trim(), case_id: caseId.trim() },
      });
      setBatches((rows) => rows.map((b) => (b.batch_id === batchId ? res.data : b)));
      if (canViewInvoices) setInvoices((rows) => rows.map((inv) => (inv.batch_id === batchId ? { ...inv, status: 'void' } : inv)));
      setNotice(`已作廢出貨批次 ${batchId}。`);
      setVoidTarget(null);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="org-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">B2B 公司</h1>
          <p className="page-subtitle">{canViewInvoices ? '公司資料、出貨批次與請款單。' : '公司資料與出貨批次。'}</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
          <button className="btn-ghost btn-sm" onClick={() => void loadCompanies(currentCursor, cursorStack)} disabled={busy}>
            重新整理
          </button>
        </div>
      </div>

      {/* 狀態(v3-dev 2026-07-18):partner 對外 API 已於 v3-dev 啟用供驗證(RA_PARTNER_API_ENABLED / restful#83);production 正式啟用仍待業主拍板商業條款。 */}
      <div className="adm-parked-banner" role="note">
        <span className="adm-parked-icon" aria-hidden="true">🅿️</span>
        <div>
          <strong>B2B 計費 — v3-dev 驗證中,production 待商業條款</strong>
          <p>
            此模組(裝置整批出貨綁定公司 + 開票收費)依 spec 01 §12。partner 對外 API 已於 v3-dev 環境啟用供驗證
            (<code>RA_PARTNER_API_ENABLED</code>,restful#83 已驗);production 正式啟用仍待業主拍板商業條款
            (費用組成／退費／稅金／partner 權限)。下方為驗證介面。
          </p>
        </div>
      </div>

      {/* 摘要統計列:共用 .kpi-strip「1 主 + N 副」——公司總數為主指標(--hero) */}
      <div className="kpi-strip" aria-label="公司統計">
        <div className="kpi-card kpi-card--hero">
          <span className="kpi-label">本頁公司</span>
          <span className="kpi-value">{companies.length}</span>
          <span className="kpi-foot">第 {cursorStack.length + 1} 頁{nextCursor ? '，尚有下一頁' : '，已到最後一頁'}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">本頁使用中</span>
          <span className="kpi-value">{activeCompanyCount}</span>
          <span className="kpi-foot">狀態為使用中</span>
        </div>
        <div className="kpi-card kpi-card--secondary">
          <span className="kpi-label">本頁本月新增</span>
          <span className="kpi-value">{newCompanyThisMonth}</span>
          <span className="kpi-foot">依建立時間所在月份計算</span>
        </div>
      </div>

      {notice && <p className="adm-action-result is-ok" role="status">{notice}</p>}
      {err && <p className="adm-action-result is-err" role="alert">{err}</p>}

      <div className="org-layout">
        <section className="card org-directory-panel">
          <div className="section-head">
            <div>
              <h2 className="section-title">公司目錄</h2>
              <span className="section-meta">
                {query ? `搜尋「${query}」 · ` : ''}第 {cursorStack.length + 1} 頁 · 本頁 {companies.length} 家公司
              </span>
            </div>
            {busy && <span className="pill pill-neutral">同步中</span>}
          </div>
          <form className="org-toolbar" onSubmit={submitSearch}>
            <input
              type="search"
              value={queryInput}
              onChange={(e) => setQueryInput(e.target.value)}
              placeholder="搜尋名稱、公司代碼、統編、聯絡人"
              aria-label="搜尋 B2B 公司"
            />
            <button className="btn-primary btn-sm" type="submit" disabled={busy}>搜尋</button>
            {(query || queryInput) && <button className="btn-ghost btn-sm" type="button" onClick={clearSearch} disabled={busy}>清除</button>}
          </form>
          {loadErr ? (
            // 載入失敗:改渲染 ErrorState(403→無權限 + request_id + 重試),不落到假空狀態
            <ErrorState error={loadErr} onRetry={retryCompanies} />
          ) : (
          <div className="table-wrap org-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>公司</th>
                  <th>狀態</th>
                  <th>統一編號</th>
                  <th>國家</th>
                  <th>建立時間</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr
                    key={c.company_id}
                    className={`clickable ${selected?.company_id === c.company_id ? 'is-selected' : ''}`}
                    onClick={() => void loadCompanyDetail(c)}
                  >
                    <td>
                      <div className="adm-cell-strong">{c.name}</div>
                      <div className="adm-mono text-tertiary">{c.company_id}</div>
                    </td>
                    <td><OrgStatus status={c.status} /></td>
                    <td>{c.tax_id || '—'}</td>
                    <td>{c.country || '—'}</td>
                    <td className="adm-mono">{formatTimestampUs(c.created_at, { second: undefined })}</td>
                  </tr>
                ))}
                {!busy && companies.length === 0 && (
                  <tr>
                    <td colSpan={5}>
                      <div className="empty-state empty-state--inline">
                        <div className="empty-state-icon">🏢</div>
                        <div className="empty-state-title">
                          {query ? '沒有符合條件的公司' : '尚無 B2B 公司'}
                        </div>
                        <div className="empty-state-detail">
                          {query
                            ? nextCursor
                              ? '本掃描頁沒有命中，後面仍有資料；請按「下一頁」繼續搜尋。'
                              : '後端已搜尋到最後一頁；請調整關鍵字，或直接新增一家符合需求的公司。'
                            : '建立第一家公司即可開始指派出貨批次與請款單。'}
                        </div>
                        {writable && (
                          <div className="empty-state-actions">
                            <button className="btn-primary btn-sm" onClick={focusCreateForm}>
                              新增公司
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {(nextCursor || cursorStack.length > 0) && (
              <div className="adm-pager">
                <button className="btn-ghost btn-sm" onClick={prevPage} disabled={cursorStack.length === 0 || busy}>
                  ← 上一頁
                </button>
                <span className="text-tertiary">第 {cursorStack.length + 1} 頁 · 本頁 {companies.length} 筆</span>
                <button className="btn-ghost btn-sm" onClick={nextPage} disabled={!nextCursor || busy}>
                  下一頁 →
                </button>
              </div>
            )}
          </div>
          )}
        </section>

        <aside className="org-side">
          <section className="card card--primary" ref={createCardRef}>
            <div className="section-head">
              <h2 className="section-title">{writable ? '新增公司' : '唯讀權限'}</h2>
            </div>
            {writable ? (
              <div className="org-create-form">
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">名稱</span>
                  <input ref={nameInputRef} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
                </label>
                <div className="org-form-row">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">統一編號</span>
                    <input value={taxId} onChange={(e) => setTaxId(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">國家</span>
                    <input value={country} onChange={(e) => setCountry(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <label className="adm-field">
                  <span className="adm-field-label text-secondary">聯絡人</span>
                  <input value={contact} onChange={(e) => setContact(e.target.value)} disabled={busy} />
                </label>
                <button className="btn-primary" disabled={busy || !name.trim()} onClick={() => void createCompany()}>
                  建立公司
                </button>
              </div>
            ) : (
              <p className="org-muted">目前角色僅能檢視公司與已授權子面。</p>
            )}
          </section>

          {/* 操作指南:僅在未選取公司時顯示,填補右欄下方空白;選取後讓 workbench 佔滿版面即移除 */}
          {!selected && (
            <section className="card org-guide-card card--muted">
              <div className="section-head">
                <h2 className="section-title">操作指南</h2>
              </div>
              <div className="org-guide-body">
                <div className="org-guide-item">
                  <span className="org-guide-item-title">統一編號格式</span>
                  <p>台灣公司請填 8 碼數字統編;海外公司可填當地稅籍編號或留空。</p>
                </div>
                <div className="org-guide-item">
                  <span className="org-guide-item-title">出貨批次守門欄位</span>
                  <p>建立出貨批次或作廢批次時,必須同時填寫「原因」與「案件編號」,系統才會放行送出。</p>
                </div>
                <div className="org-guide-item">
                  <span className="org-guide-item-title">公司狀態意涵</span>
                  <p><span className="pill pill-good">使用中</span> 表示可正常出貨與請款;<span className="pill pill-warn">暫停</span> 表示暫停往來,新出貨批次應審慎評估。</p>
                </div>
              </div>
            </section>
          )}
        </aside>
      </div>

      {selected && (
        <section className="workbench-panel org-workbench" ref={workbenchRef}>
          <div className="workbench-panel-head org-workbench-head">
            <div>
              <h2 className="org-workbench-title">{selected.name}</h2>
              <span className="adm-mono text-tertiary">{selected.company_id}</span>
            </div>
            <div className="org-workbench-meta">
              <OrgStatus status={selected.status} />
              <span className="pill pill-neutral">{selected.country || '—'}</span>
              {detailLoading && <span className="pill pill-neutral">同步中</span>}
            </div>
          </div>

          <div className="adm-tabs org-tabs" role="tablist" aria-label="公司分頁">
            {(['profile', 'batches', ...(canViewInvoices ? ['invoices' as const] : [])] as OrgTab[]).map((tab) => (
              <button
                key={tab}
                className={`adm-tab ${activeTab === tab ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab)}
                role="tab"
                aria-selected={activeTab === tab}
              >
                {tab === 'profile' ? '總覽' : tab === 'batches' ? '出貨批次' : '請款單'}
              </button>
            ))}
          </div>

          {activeTab === 'profile' && (
            <div className="kpi-strip co-profile-strip">
              {detailLoading ? (
                <>
                  {[0, 1, 2, 3].map((i) => (
                    <div className="kpi-card" key={i}>
                      <div className="skeleton-bar" style={{ width: '40%' }} />
                      <div className="skeleton-bar" style={{ width: '70%', height: 22 }} />
                      <div className="skeleton-bar" style={{ width: '55%' }} />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="kpi-card">
                    <span className="kpi-label">聯絡人</span>
                    <span className="kpi-value co-profile-value">{selected.contact || '—'}</span>
                    <span className="kpi-foot">統編 {selected.tax_id || '—'}</span>
                  </div>
                  <div className="kpi-card">
                    <span className="kpi-label">出貨批次</span>
                    <span className="kpi-value co-profile-value">{activeBatchCount} / {batches.length}</span>
                    <span className="kpi-foot">共 {batchDeviceCount} 台裝置</span>
                  </div>
                  {canViewInvoices && (
                    <div className="kpi-card">
                      <span className="kpi-label">請款單</span>
                      <span className="kpi-value co-profile-value">{invoices.length}</span>
                      <span className="kpi-foot">{invoiceTotal(invoices)}</span>
                    </div>
                  )}
                  <div className="kpi-card">
                    <span className="kpi-label">更新時間</span>
                    <span className="kpi-value co-profile-value adm-mono">{formatTimestampUs(selected.updated_at || selected.created_at, { second: undefined })}</span>
                    <span className="kpi-foot">建立於 {formatTimestampUs(selected.created_at, { second: undefined })}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'batches' && (
            <div className="org-tab-panel">
              {writable && (
                <section className="org-inline-form">
                  <div className="inline-notice inline-notice--warn">
                    建立出貨批次將同時產生對應請款單,請先填寫「原因」與「案件編號」再送出。
                  </div>
                  <div className="org-form-row">
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">原因</span>
                      <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} />
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">案件編號</span>
                      <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busy} />
                    </label>
                  </div>
                  <div className="org-form-row">
                    <label className="adm-field org-devices-field">
                      <span className="adm-field-label text-secondary">裝置 ID</span>
                      <textarea value={deviceIds} onChange={(e) => setDeviceIds(e.target.value)} rows={4} disabled={busy} />
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">金額</span>
                      <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={busy} />
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">幣別</span>
                      <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} disabled={busy} />
                    </label>
                    <label className="adm-field org-desc-field">
                      <span className="adm-field-label text-secondary">描述</span>
                      <input value={desc} onChange={(e) => setDesc(e.target.value)} disabled={busy} />
                    </label>
                    <button className="btn-primary" disabled={busy || !deviceIds.trim() || !hasContext} onClick={() => void createBatch()}>
                      建立批次
                    </button>
                  </div>
                </section>
              )}

              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>批次</th>
                      <th>狀態</th>
                      <th>裝置數</th>
                      {canViewInvoices && <th>請款單</th>}
                      <th>建立時間</th>
                      <th aria-label="操作" />
                    </tr>
                  </thead>
                  <tbody>
                    {batches.map((b) => (
                      <tr key={b.batch_id}>
                        <td>
                          <div className="adm-mono">{b.batch_id}</div>
                          <div className="text-tertiary">{b.description || '—'}</div>
                        </td>
                        <td><OrgStatus status={b.status} /></td>
                        <td>{b.device_ids?.length ?? 0}</td>
                        {canViewInvoices && <td className="adm-mono">{b.invoice_id || '—'}</td>}
                        <td className="adm-mono">{formatTimestampUs(b.created_at, { second: undefined })}</td>
                        <td>
                          <button
                            className="btn-ghost btn-sm btn-ghost-danger"
                            disabled={!writable || busy || b.status === 'voided' || !hasContext}
                            onClick={() => { setErr(''); setVoidTarget(b); }}
                          >
                            作廢
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!detailLoading && batches.length === 0 && (
                      <tr>
                        <td colSpan={canViewInvoices ? 6 : 5}>
                          <div className="adm-inline-empty">
                            <span className="adm-inline-empty-icon">📦</span>
                            <span>此公司尚無出貨批次,由上方表單建立。</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {canViewInvoices && activeTab === 'invoices' && (
            <div className="org-tab-panel">
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>請款單</th>
                      <th>狀態</th>
                      <th>金額</th>
                      <th>批次</th>
                      <th>建立時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.invoice_id}>
                        <td className="adm-mono">{inv.invoice_id}</td>
                        <td><OrgStatus status={inv.status} /></td>
                        <td>{money(inv)}</td>
                        <td className="adm-mono">{inv.batch_id || '—'}</td>
                        <td className="adm-mono">{formatTimestampUs(inv.created_at, { second: undefined })}</td>
                      </tr>
                    ))}
                    {!detailLoading && invoices.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          <div className="adm-inline-empty">
                            <span className="adm-inline-empty-icon">🧾</span>
                            <span>尚無請款單,將於建立出貨批次後自動產生。</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {voidTarget && (
        <ConfirmDialog
          title="作廢出貨批次"
          tone="danger"
          confirmLabel="確認作廢"
          busy={busy}
          error={err || undefined}
          warning="此操作不可復原:將作廢此出貨批次,並連動把其請款單標記為 void。"
          body={
            <>
              <p>
                批次 <span className="adm-mono">{voidTarget.batch_id}</span>
                （{voidTarget.device_ids?.length ?? 0} 台裝置{voidTarget.invoice_id ? `、請款單 ${voidTarget.invoice_id}` : ''}）將被作廢。
              </p>
              <p className="text-tertiary">處置理由:{reason.trim()}・案件:{caseId.trim()}(將寫入稽核)</p>
            </>
          }
          onConfirm={() => void voidBatch(voidTarget.batch_id)}
          onCancel={() => setVoidTarget(null)}
        />
      )}
    </div>
  );
}
